import type { InfoMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import tbclEntries from "@-/com.npmjs.leonsilicon__tbcl";
import onetime from "@-/com.npmjs.onetime";
import { useMemo } from "react";

const getTbclLevelByCharacter = onetime((): ReadonlyMap<string, number> => {
  const map = new Map<string, number>();
  for (const [, traditional, level] of tbclEntries) {
    const chars = traditional.includes("/") ? traditional.split("/") : [traditional];
    for (const c of chars) {
      if (c.length === 0) {
        continue;
      }
      const existing = map.get(c);
      if (existing === undefined || level < existing) {
        map.set(c, level);
      }
    }
  }
  return map;
});

function cardHskLevelForDeviceVersion(
  infoBackCardSlice: InfoMorphemeSlice,
  hskVersion: string,
): number | null {
  switch (hskVersion) {
    case "hsk2": {
      return infoBackCardSlice.hsk2010Level ?? null;
    }
    case "hsk3": {
      return infoBackCardSlice.hsk2021Level ?? null;
    }
    case "hsk2026": {
      return infoBackCardSlice.hsk2026Level ?? null;
    }
    default: {
      return null;
    }
  }
}

/**
 * The card's curriculum grade as a badge label: `TBCL n` when the user studies traditional
 * characters, otherwise `HSK n` for their selected HSK version. `null` when the card is outside
 * that curriculum, so the caller falls back to its own label (or shows no badge).
 */
export function useMorphemeInfoPanelDictionaryGradeLabel(
  infoBackCardSlice: InfoMorphemeSlice,
): string | null {
  const { hanziScript, hskVersion } = useActorUser();

  return useMemo(() => {
    if (hanziScript === "traditional") {
      const character =
        infoBackCardSlice.morphemeType === "grapheme"
          ? infoBackCardSlice.grapheme
          : infoBackCardSlice.word;
      const level = getTbclLevelByCharacter().get(character) ?? null;
      return level === null ? null : `TBCL ${level}`;
    }
    const level = cardHskLevelForDeviceVersion(infoBackCardSlice, hskVersion);
    return level === null ? null : `HSK ${level}`;
  }, [hanziScript, hskVersion, infoBackCardSlice]);
}
